/**
 * useClinic — tiny cached-clinic hook.
 *
 * `/api/clinics/me` is called by many surfaces (calendar, waiting room,
 * settings, billing). This hook shares one fetch across the app so the
 * clinic doc (and its `settings.workflow_mode`) is loaded ONCE per session,
 * refetched on demand.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;
const _listeners = new Set();

async function _fetch() {
  const { data } = await api.get("/clinics/me");
  _cache = data;
  _listeners.forEach((fn) => fn(data));
  return data;
}

export function useClinic() {
  const [clinic, setClinic] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    const listener = (c) => setClinic(c);
    _listeners.add(listener);
    if (!_cache) {
      setLoading(true);
      _fetch()
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    return () => _listeners.delete(listener);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { return await _fetch(); }
    finally { setLoading(false); }
  }, []);

  return { clinic, loading, refresh };
}

/** Explicit setter for mutations that already have a fresh clinic doc. */
export function primeClinicCache(newClinic) {
  _cache = newClinic;
  _listeners.forEach((fn) => fn(newClinic));
}
